import { z } from 'zod';
import { coreIds } from './ids.js';
import {
  ASSOCIATION_KIND_AGENT_GROUP,
  associationDataString,
  type AgentAssociation,
  type AgentAssociationRef,
} from './agent-association.types.js';

/**
 * User-defined groupings of agents ("payments migration", "flaky test hunt").
 * A group is a named label private to its creator within an organization;
 * membership rides on the existing
 * association table as a `agent_group` association with the `member` role, so
 * a group shows up as a chip on the agent row, is filterable in the agent
 * list, and needs no second hydration path.
 *
 * Split of responsibilities:
 * - `agent_groups` owns group identity (id + owner + name), so a group
 *   survives a rename and can exist with no members;
 * - `agent_associations` owns membership: one row per (agent, group), with
 *   the group id as {@link AgentAssociationRef.externalId} and the group name
 *   mirrored into `data.name` for display.
 *
 * Membership is exclusive: an agent is in at most one group, so filing it
 * into another moves it. A group answers "where does this run live", and a
 * run that lives in two places shows two chips and appears twice in a
 * sidebar grouped by folder. Labels, which describe a run rather than file
 * it, are the multi-valued taxonomy.
 */

/** The `data` key the group's display name is mirrored under. */
export const AGENT_GROUP_NAME_KEY = 'name';

/**
 * The role every membership row carries. Groups have exactly one kind of
 * relationship, but `role` is part of the association table's uniqueness key,
 * so it has to be a fixed, shared value rather than an incidental one.
 */
export const AGENT_GROUP_MEMBER_ROLE = 'member' as const;

/**
 * Group names are short human labels, unique per user and organization
 * (case-insensitively — "Payments" and "payments" are the same group).
 */
export const AGENT_GROUP_NAME_MAX_LENGTH = 60;

export const AgentGroupNameSchema = z
  .string()
  .trim()
  .min(1, 'Group name is required')
  .max(
    AGENT_GROUP_NAME_MAX_LENGTH,
    `Group name must be ${AGENT_GROUP_NAME_MAX_LENGTH} characters or fewer`,
  );

export const AgentGroupSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    /** Number of agents currently in the group. */
    memberCount: z.number(),
    createdAt: z.number(),
  })
  .meta({ id: 'AgentGroup' });
export type AgentGroup = z.infer<typeof AgentGroupSchema>;

export const ListAgentGroupsResponseSchema = z
  .object({ groups: z.array(AgentGroupSchema) })
  .meta({ id: 'ListAgentGroupsResponse' });
export type ListAgentGroupsResponse = z.infer<typeof ListAgentGroupsResponseSchema>;

/**
 * Create a group, optionally seeding it with the agents selected in the list
 * — the one-step "select agents → new group" gesture, which would otherwise
 * need a create call followed by a membership call the UI could half-apply.
 */
export const CreateAgentGroupRequestSchema = z
  .object({
    name: AgentGroupNameSchema,
    agentIds: z.array(z.string()).optional(),
  })
  .meta({ id: 'CreateAgentGroupRequest' });
export type CreateAgentGroupRequest = z.infer<typeof CreateAgentGroupRequestSchema>;

export const UpdateAgentGroupRequestSchema = z
  .object({ name: AgentGroupNameSchema })
  .meta({ id: 'UpdateAgentGroupRequest' });
export type UpdateAgentGroupRequest = z.infer<typeof UpdateAgentGroupRequestSchema>;

export const AgentGroupResponseSchema = z
  .object({ group: AgentGroupSchema })
  .meta({ id: 'AgentGroupResponse' });
export type AgentGroupResponse = z.infer<typeof AgentGroupResponseSchema>;

/** Add or remove a set of agents in one call — the list acts on selections. */
export const AgentGroupMembersRequestSchema = z
  .object({ agentIds: z.array(z.string()).min(1) })
  .meta({ id: 'AgentGroupMembersRequest' });
export type AgentGroupMembersRequest = z.infer<typeof AgentGroupMembersRequestSchema>;

export const AgentGroupParamsSchema = z.object({ groupId: coreIds.schema('agentGroup') });
export type AgentGroupParams = z.infer<typeof AgentGroupParamsSchema>;

/** A group an agent belongs to, as read off its association refs. */
export interface AgentGroupRef {
  id: string;
  name: string;
}

/** Whether an association ref is a group membership. */
export function isAgentGroupAssociation(ref: Pick<AgentAssociationRef, 'kind'>): boolean {
  return ref.kind === ASSOCIATION_KIND_AGENT_GROUP;
}

/**
 * Display name of a group membership: the name mirrored into `data` at write
 * time, falling back to the group id so a chip written before the mirror (or
 * by a client that omitted it) still renders something addressable.
 */
export function agentGroupRefName(ref: AgentAssociationRef): string {
  return associationDataString(ref, AGENT_GROUP_NAME_KEY) ?? ref.externalId;
}

/**
 * The groups an agent belongs to, in the order its associations were
 * recorded. Works off the raw `associations` rather than the display refs so
 * callers that only need membership (filtering, the group view) don't pay for
 * the legacy `prUrl` synthesis.
 *
 * Membership is exclusive, so this holds at most one entry; prefer
 * {@link agentGroupRef} unless you are genuinely iterating. It stays plural
 * because rows written before that rule can still carry more than one.
 */
export function agentGroupRefs(agent: {
  associations?: AgentAssociation[] | null;
}): AgentGroupRef[] {
  return (agent.associations ?? [])
    .filter((a) => a.kind === ASSOCIATION_KIND_AGENT_GROUP)
    .map((a) => ({ id: a.externalId, name: agentGroupRefName(a) }));
}

/**
 * The one group an agent is filed under, or null. Groups are exclusive — a
 * run lives in a single folder — so every surface that shows "the group"
 * (the chip, the sidebar section, a relaunch's defaults) reads this rather
 * than picking an entry out of the list itself.
 */
export function agentGroupRef(agent: {
  associations?: AgentAssociation[] | null;
}): AgentGroupRef | null {
  return agentGroupRefs(agent)[0] ?? null;
}

/** Whether the agent is a member of `groupId`. */
export function agentInGroup(
  agent: { associations?: AgentAssociation[] | null },
  groupId: string,
): boolean {
  return (agent.associations ?? []).some(
    (a) => a.kind === ASSOCIATION_KIND_AGENT_GROUP && a.externalId === groupId,
  );
}
